import React, { useState, useMemo } from 'react'
import { plantsByCategory, allPlants, PlantData } from '../data/plants-complete'
import { getEmojiByName } from '../data/plantEmojis'
import DiseasePestGuide from '../components/DiseasePestGuide'

// Categories with emojis (using widely supported emojis)
const CATEGORIES = [
  { id: 'all', name: 'All Categories', emoji: '🍂' },
  { id: 'vegetable', name: 'Vegetables', emoji: '🥬' },
  { id: 'herb', name: 'Herbs', emoji: '🌿' },
  { id: 'fruit', name: 'Fruits', emoji: '🍎' },
  { id: 'berry', name: 'Berries', emoji: '🍇' },
  { id: 'flower', name: 'Flowers', emoji: '🌸' },
  { id: 'tree', name: 'Trees', emoji: '🌳' },
  { id: 'allium', name: 'Alliums', emoji: '🧅' },
  { id: 'cucurbit', name: 'Squash & Cucurbits', emoji: '🎃' },
  { id: 'legume', name: 'Legumes', emoji: '🥜' },
  { id: 'grain', name: 'Grains', emoji: '🌾' },
  { id: 'microgreen', name: 'Microgreens', emoji: '🌿' },
  { id: 'sprout', name: 'Sprouts', emoji: '🌱' },
  { id: 'cover crop', name: 'Cover Crops', emoji: '🌾' },
  { id: 'mushroom', name: 'Mushrooms', emoji: '🍄' },
]

// Subcategories for vegetables
const VEGETABLE_SUBCATEGORIES = [
  { id: 'tomato', name: 'Tomatoes', emoji: '🍅' },
  { id: 'pepper', name: 'Peppers', emoji: '🫑' },
  { id: 'lettuce', name: 'Lettuce', emoji: '🥬' },
  { id: 'spinach', name: 'Spinach', emoji: '🍃' },
  { id: 'kale', name: 'Kale & Collards', emoji: '🥬' },
  { id: 'chard', name: 'Swiss Chard', emoji: '🌿' },
  { id: 'cabbage', name: 'Cabbage', emoji: '🥬' },
  { id: 'broccoli', name: 'Broccoli & Cauliflower', emoji: '🥦' },
  { id: 'carrot', name: 'Carrots', emoji: '🥕' },
  { id: 'radish', name: 'Radishes', emoji: '🔴' },
  { id: 'beet', name: 'Beets', emoji: '🔴' },
  { id: 'turnip', name: 'Turnips & Rutabagas', emoji: '⚪' },
  { id: 'onion', name: 'Onions', emoji: '🧅' },
  { id: 'garlic', name: 'Garlic', emoji: '🧄' },
  { id: 'potato', name: 'Potatoes', emoji: '🥔' },
  { id: 'squash', name: 'Squash', emoji: '🎃' },
  { id: 'zucchini', name: 'Zucchini', emoji: '🥒' },
  { id: 'cucumber', name: 'Cucumbers', emoji: '🥒' },
  { id: 'bean', name: 'Beans', emoji: '🟢' },
  { id: 'pea', name: 'Peas', emoji: '🟢' },
  { id: 'corn', name: 'Corn', emoji: '🌽' },
  { id: 'eggplant', name: 'Eggplant', emoji: '🍆' },
  { id: 'okra', name: 'Okra', emoji: '🟢' },
  { id: 'asparagus', name: 'Asparagus', emoji: '🌱' },
  { id: 'other', name: 'Other Vegetables', emoji: '🥬' },
]

// Hardiness zones
const ZONES = [
  { id: '', name: 'All Zones' },
  { id: '3', name: 'Zone 3 (-40°F)' },
  { id: '4', name: 'Zone 4 (-30°F)' },
  { id: '5', name: 'Zone 5 (-20°F)' },
  { id: '6', name: 'Zone 6 (-10°F)' },
  { id: '7', name: 'Zone 7 (0°F)' },
  { id: '8', name: 'Zone 8 (10°F)' },
  { id: '9', name: 'Zone 9 (20°F)' },
  { id: '10', name: 'Zone 10 (30°F)' },
  { id: '11', name: 'Zone 11 (40°F)' },
  { id: '12', name: 'Zone 12 (50°F)' },
  { id: '13', name: 'Zone 13 (60°F)' },
]

// Sun requirements
const SUN_OPTIONS = [
  { id: '', name: 'Any Sun' },
  { id: 'full', name: '☀️ Full Sun' },
  { id: 'partial', name: '⛅ Partial Sun' },
  { id: 'shade', name: '🌑 Shade' },
]

// Water requirements
const WATER_OPTIONS = [
  { id: '', name: 'Any Water' },
  { id: 'low', name: '💧 Low' },
  { id: 'medium', name: '💧💧 Medium' },
  { id: 'high', name: '💧💧💧 High' },
]

const PlantDatabase: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'plants' | 'diseases'>('plants')
  
  // Step-by-step filters
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('')
  const [selectedZone, setSelectedZone] = useState<string>('')
  const [selectedSun, setSelectedSun] = useState<string>('')
  const [selectedWater, setSelectedWater] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState('')
  
  const [selectedPlant, setSelectedPlant] = useState<PlantData | null>(null)

  // Get plants based on category selection
  const getPlantsForCategory = (category: string): PlantData[] => {
    if (!category || category === 'all') return allPlants
    
    // Special case for legumes - they're still in vegetables
    if (category === 'legume') {
      return allPlants.filter(p => 
        p.name.toLowerCase().includes('bean') ||
        p.name.toLowerCase().includes('pea') ||
        p.name.toLowerCase().includes('lentil') ||
        p.name.toLowerCase().includes('chickpea') ||
        p.name.toLowerCase().includes('fava') ||
        p.name.toLowerCase().includes('soybean') ||
        p.name.toLowerCase().includes('edamame') ||
        p.name.toLowerCase().includes('cowpea') ||
        p.name.toLowerCase().includes('mung bean') ||
        p.name.toLowerCase().includes('adzuki')
      )
    }
    
    // Map category names to plantsByCategory keys
    const categoryMap: Record<string, string> = {
      'vegetable': 'vegetables',
      'herb': 'herbs',
      'fruit': 'fruits',
      'berry': 'berries',
      'flower': 'flowers',
      'tree': 'trees',
      'allium': 'alliums',
      'cucurbit': 'cucurbits',
      'grain': 'grains',
      'microgreen': 'microgreens',
      'sprout': 'sprouts',
      'cover crop': 'coverCrops',
      'mushroom': 'mushrooms',
    }
    const key = categoryMap[category] || category
    return (plantsByCategory as any)[key] || []
  }

  // Filter plants step by step
  const filteredPlants = useMemo(() => {
    let result = getPlantsForCategory(selectedCategory)
    
    // Apply subcategory filter (for vegetables)
    if (selectedCategory === 'vegetable' && selectedSubcategory) {
      result = result.filter(plant => {
        const name = plant.name.toLowerCase()
        if (selectedSubcategory === 'other') {
          // Return plants that don't match other subcategories
          const knownTypes = ['tomato', 'pepper', 'lettuce', 'spinach', 'kale', 'chard', 'cabbage', 'broccoli', 'cauliflower', 'carrot', 'radish', 'beet', 'turnip', 'rutabaga', 'onion', 'garlic', 'potato', 'squash', 'zucchini', 'cucumber', 'bean', 'pea', 'corn', 'eggplant', 'okra', 'asparagus']
          return !knownTypes.some(type => name.includes(type))
        }
        return name.includes(selectedSubcategory)
      })
    }
    
    // Apply zone filter
    if (selectedZone) {
      result = result.filter(plant => {
        if (!plant.hardinessZones) return true
        const zones = plant.hardinessZones.split('-').map(z => parseInt(z.replace(/[a-b]/i, '')))
        const minZone = Math.min(...zones)
        const maxZone = Math.max(...zones)
        const userZone = parseInt(selectedZone)
        return userZone >= minZone && userZone <= maxZone
      })
    }
    
    // Apply sun filter
    if (selectedSun) {
      result = result.filter(plant => plant.sun === selectedSun)
    }
    
    // Apply water filter
    if (selectedWater) {
      result = result.filter(plant => plant.water === selectedWater)
    }
    
    // Apply search
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      result = result.filter(plant => 
        plant.name.toLowerCase().includes(term) ||
        (plant.scientificName && plant.scientificName.toLowerCase().includes(term)) ||
        plant.description.toLowerCase().includes(term)
      )
    }
    
    return result
  }, [selectedCategory, selectedSubcategory, selectedZone, selectedSun, selectedWater, searchTerm])

  // Get category count
  const getCategoryCount = (categoryId: string) => {
    if (categoryId === 'legume') {
      return allPlants.filter(p => 
        p.name.toLowerCase().includes('bean') ||
        p.name.toLowerCase().includes('pea') ||
        p.name.toLowerCase().includes('lentil') ||
        p.name.toLowerCase().includes('chickpea') ||
        p.name.toLowerCase().includes('fava') ||
        p.name.toLowerCase().includes('soybean') ||
        p.name.toLowerCase().includes('edamame') ||
        p.name.toLowerCase().includes('cowpea') ||
        p.name.toLowerCase().includes('mung bean') ||
        p.name.toLowerCase().includes('adzuki')
      ).length
    }
    return getPlantsForCategory(categoryId).length
  }

  // Reset all filters
  const resetFilters = () => {
    setSelectedCategory('')
    setSelectedSubcategory('')
    setSelectedZone('')
    setSelectedSun('')
    setSelectedWater('')
    setSearchTerm('')
  }

  // Get subcategory count
  const getSubcategoryCount = (subId: string) => {
    if (!selectedCategory) return 0
    let plants = getPlantsForCategory(selectedCategory)
    if (subId === 'other') {
      const knownTypes = ['tomato', 'pepper', 'lettuce', 'spinach', 'kale', 'chard', 'cabbage', 'broccoli', 'cauliflower', 'carrot', 'radish', 'beet', 'turnip', 'rutabaga', 'onion', 'garlic', 'potato', 'squash', 'zucchini', 'cucumber', 'bean', 'pea', 'corn', 'eggplant', 'okra', 'asparagus']
      return plants.filter(p => !knownTypes.some(type => p.name.toLowerCase().includes(type))).length
    }
    return plants.filter(p => p.name.toLowerCase().includes(subId)).length
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      <div className="card">
        <h2>🌱 Plant Database</h2>
        <p>Browse our collection of {allPlants.length.toLocaleString()} plants. Use the filters below to narrow your search.</p>
      </div>

      {/* Tabs */}
      <div className="card" style={{ padding: '0.5rem', marginTop: '1rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className="btn"
            style={{
              background: activeTab === 'plants' ? '#2d5a27' : 'white',
              color: activeTab === 'plants' ? 'white' : '#333',
              flex: 1
            }}
            onClick={() => setActiveTab('plants')}
          >
            🌱 Plants ({allPlants.length.toLocaleString()})
          </button>
          <button
            className="btn"
            style={{
              background: activeTab === 'diseases' ? '#2d5a27' : 'white',
              color: activeTab === 'diseases' ? 'white' : '#333',
              flex: 1
            }}
            onClick={() => setActiveTab('diseases')}
          >
            🐛 Pests & Diseases
          </button>
        </div>
      </div>

      {activeTab === 'plants' ? (
        <>
          {/* Step 1: Category Selection */}
          <div className="card" style={{ marginTop: '1rem' }}>
            <h3 style={{ marginBottom: '1rem', color: '#2d5a27' }}>
              Step 1: Choose a Category
            </h3>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', 
              gap: '0.75rem' 
            }}>
              {CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => {
                    setSelectedCategory(cat.id === 'all' ? '' : cat.id)
                    setSelectedSubcategory('')
                  }}
                  style={{
                    padding: '1rem',
                    borderRadius: '8px',
                    border: `2px solid ${selectedCategory === cat.id || (cat.id === 'all' && !selectedCategory) ? '#22c55e' : '#e5e7eb'}`,
                    background: selectedCategory === cat.id || (cat.id === 'all' && !selectedCategory) ? '#f0fdf4' : 'white',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ fontSize: '2rem', marginBottom: '0.25rem' }}>{cat.emoji}</div>
                  <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>{cat.name}</div>
                  <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                    {getCategoryCount(cat.id).toLocaleString()} plants
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Step 2: Vegetable Subcategory (only if vegetables selected) */}
          {selectedCategory === 'vegetable' && (
            <div className="card" style={{ marginTop: '1rem' }}>
              <h3 style={{ marginBottom: '1rem', color: '#2d5a27' }}>
                Step 2: Choose Vegetable Type
              </h3>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', 
                gap: '0.5rem' 
              }}>
                {VEGETABLE_SUBCATEGORIES.map(sub => (
                  <button
                    key={sub.id}
                    onClick={() => setSelectedSubcategory(selectedSubcategory === sub.id ? '' : sub.id)}
                    style={{
                      padding: '0.75rem',
                      borderRadius: '6px',
                      border: `2px solid ${selectedSubcategory === sub.id ? '#22c55e' : '#e5e7eb'}`,
                      background: selectedSubcategory === sub.id ? '#f0fdf4' : 'white',
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>{sub.emoji}</div>
                    <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{sub.name}</div>
                    <div style={{ fontSize: '0.75rem', color: '#666' }}>
                      {getSubcategoryCount(sub.id)} varieties
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Additional Filters */}
          {selectedCategory && (
            <div className="card" style={{ marginTop: '1rem' }}>
              <h3 style={{ marginBottom: '1rem', color: '#2d5a27' }}>
                Step 3: Narrow Your Search (Optional)
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                {/* Zone Filter */}
                <div>
                  <label style={{ fontSize: '0.85rem', fontWeight: 500, display: 'block', marginBottom: '0.25rem' }}>
                    Hardiness Zone
                  </label>
                  <select
                    value={selectedZone}
                    onChange={(e) => setSelectedZone(e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                  >
                    {ZONES.map(zone => (
                      <option key={zone.id} value={zone.id}>{zone.name}</option>
                    ))}
                  </select>
                </div>

                {/* Sun Filter */}
                <div>
                  <label style={{ fontSize: '0.85rem', fontWeight: 500, display: 'block', marginBottom: '0.25rem' }}>
                    Sun Requirements
                  </label>
                  <select
                    value={selectedSun}
                    onChange={(e) => setSelectedSun(e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                  >
                    {SUN_OPTIONS.map(opt => (
                      <option key={opt.id} value={opt.id}>{opt.name}</option>
                    ))}
                  </select>
                </div>

                {/* Water Filter */}
                <div>
                  <label style={{ fontSize: '0.85rem', fontWeight: 500, display: 'block', marginBottom: '0.25rem' }}>
                    Water Needs
                  </label>
                  <select
                    value={selectedWater}
                    onChange={(e) => setSelectedWater(e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                  >
                    {WATER_OPTIONS.map(opt => (
                      <option key={opt.id} value={opt.id}>{opt.name}</option>
                    ))}
                  </select>
                </div>

                {/* Search */}
                <div>
                  <label style={{ fontSize: '0.85rem', fontWeight: 500, display: 'block', marginBottom: '0.25rem' }}>
                    Search by Name
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 'Roma tomato'..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                </div>
              </div>

              {/* Reset button */}
              <button
                onClick={resetFilters}
                style={{
                  marginTop: '1rem',
                  padding: '0.5rem 1rem',
                  background: '#f3f4f6',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                🔄 Reset All Filters
              </button>
            </div>
          )}

          {/* Results Section */}
          {selectedCategory && (
            <div className="card" style={{ marginTop: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0, color: '#2d5a27' }}>
                  {filteredPlants.length.toLocaleString()} Plants Found
                </h3>
                <span style={{ color: '#666', fontSize: '0.9rem' }}>
                  Click any plant for details
                </span>
              </div>

              {filteredPlants.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
                  <p>No plants match your filters. Try adjusting your search criteria.</p>
                  <button onClick={resetFilters} className="btn btn-primary" style={{ marginTop: '1rem' }}>
                    Reset Filters
                  </button>
                </div>
              ) : (
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
                  gap: '1rem' 
                }}>
                  {filteredPlants.slice(0, 100).map(plant => (
                    <div
                      key={plant.id}
                      onClick={() => setSelectedPlant(plant)}
                      style={{
                        padding: '1rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        background: 'white'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#22c55e'
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(34, 197, 94, 0.2)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e5e7eb'
                        e.currentTarget.style.boxShadow = 'none'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <span style={{ fontSize: '2rem' }}>{getEmojiByName(plant.name)}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {plant.name}
                          </div>
                          {plant.scientificName && (
                            <div style={{ fontSize: '0.8rem', color: '#888', fontStyle: 'italic' }}>
                              {plant.scientificName}
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        gap: '0.5rem', 
                        marginTop: '0.75rem',
                        flexWrap: 'wrap'
                      }}>
                        <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: '#f3f4f6', borderRadius: '4px' }}>
                          {plant.sun === 'full' ? '☀️' : plant.sun === 'partial' ? '⛅' : '🌑'} {plant.sun}
                        </span>
                        <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: '#f3f4f6', borderRadius: '4px' }}>
                          💧 {plant.water}
                        </span>
                        <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: '#f3f4f6', borderRadius: '4px' }}>
                          🌡️ {plant.hardinessZones || 'All zones'}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem', lineHeight: 1.4 }}>
                        {plant.description.slice(0, 80)}...
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {filteredPlants.length > 100 && (
                <div style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>
                  Showing first 100 of {filteredPlants.length.toLocaleString()} plants. 
                  Use filters to narrow your search.
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <DiseasePestGuide />
      )}

      {/* Plant Detail Modal */}
      {selectedPlant && (
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: '1rem'
          }}
          onClick={() => setSelectedPlant(null)}
        >
          <div 
            style={{
              background: 'white',
              borderRadius: '12px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              padding: '1.5rem'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <span style={{ fontSize: '3rem' }}>{getEmojiByName(selectedPlant.name)}</span>
                <div>
                  <h2 style={{ margin: 0, color: '#2d5a27' }}>{selectedPlant.name}</h2>
                  {selectedPlant.scientificName && (
                    <div style={{ fontStyle: 'italic', color: '#888' }}>{selectedPlant.scientificName}</div>
                  )}
                </div>
              </div>
              <button 
                onClick={() => setSelectedPlant(null)}
                style={{ fontSize: '1.5rem', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>

            <div style={{ lineHeight: 1.6 }}>
              <p>{selectedPlant.description}</p>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginTop: '1rem' }}>
                <div>
                  <strong>☀️ Sun:</strong> {selectedPlant.sun}
                </div>
                <div>
                  <strong>💧 Water:</strong> {selectedPlant.water}
                </div>
                <div>
                  <strong>🌡️ Zones:</strong> {selectedPlant.hardinessZones || 'All'}
                </div>
                <div>
                  <strong>📅 Days to Harvest:</strong> {selectedPlant.daysToHarvest}
                </div>
                <div>
                  <strong>📏 Spacing:</strong> {selectedPlant.spacing}" × {selectedPlant.rowSpacing}"
                </div>
                <div>
                  <strong>🌱 Germination:</strong> {selectedPlant.daysToGermination} days
                </div>
              </div>

              {selectedPlant.companions && selectedPlant.companions.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <strong style={{ color: '#4caf50' }}>✓ Good Companions:</strong>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                    {selectedPlant.companions.map((companion, idx) => (
                      <span key={idx} style={{ background: '#e8f5e9', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.85rem' }}>
                        {companion}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedPlant.enemies && selectedPlant.enemies.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <strong style={{ color: '#f44336' }}>✗ Avoid Planting Near:</strong>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                    {selectedPlant.enemies.map((enemy, idx) => (
                      <span key={idx} style={{ background: '#ffebee', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.85rem' }}>
                        {enemy}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PlantDatabase
